import { Issue, Waiver } from '../headersegment/interfaces';
import { ISSUE_ACTION_RES_ALL_OPTIONS } from '../defineTdm';
import moment from 'moment';

interface Event {
  comment?: string;
  change?: string;
  state?: string;
  type?: string;
  prettyType?: string;
  subResolution?: string;
  optInTraveler?: boolean;
  createTime?: string;
  extraMinutesAdded?: number;
  extraMinutesAvoided?: number;
  travelerContacted?: boolean;
  pnrUpdated?: boolean;
  overnightAvoided?: boolean;
  ratePaidInUsDollars?: number;
  flightsChanged?: boolean;
  hotelChanged?: boolean;
  newFlightsOnSameCarrier?: boolean;
  groundTransportationChanged?: boolean;
  recipientAddress?: string;
  resolveByTime?: string;
}

interface Permission {
  name?: string;
}

interface OrgNodeObj {
  name?: string;
  level?: number;
}

export interface Flight {
  departureAirportFull?: string;
  departureAirportCode: string;
  departureAirportCityState?: string;
  departureAirportCodeCityStateCountry?: string;
  arrivalAirportFull?: string;
  arrivalAirportCode: string;
  arrivalAirportCityState?: string;
  arrivalAirportCodeCityStateCountry?: string;
}

interface Airline {
  full?: string;
  code: string;
  name?: string;
}

interface FlightKey {
  airlineCode: string;
  flightNumber: string;
  departureAirportCode: string;
  arrivalAirportCode: string;
}

interface Passenger {
  firstName?: string;
  lastName?: string;
}

interface Hop {
  actualGateDepartureDateTime?: string;
  actualGateArrivalDateTime?: string;
  estimatedGateDepartureDateTime?: string;
  estimatedGateArrivalDateTime?: string;
  scheduledGateDepartureDateTime?: string;
  scheduledGateArrivalDateTime?: string;
  actualRunwayDepartureDateTime?: string;
  actualRunwayArrivalDateTime?: string;
  estimatedRunwayDepartureDateTime?: string;
  estimatedRunwayArrivalDateTime?: string;
  departureTime?: string;
  arrivalTime?: string;
}

interface Connection {
  in?: string;
  inFull?: string;
  connectionTime?: number;
  minConnectionTime?: number;
  departureGate?: string;
  arrivalGate?: string;
}

interface Stop {
  in?: string;
}

export type DepArr = 'dep' | 'arr';

const DEFAULT = 'n/a'

const generateDateString = (firstDate: string, secondDate?: string): string => {
  const first = moment(firstDate);
  if (!secondDate) {
    return `${first.format('MMM Do, YYYY')} and beyond`;
  }

  const second = moment(secondDate);
  if (first.isSame(second)) {
    return first.format('MMM Do, YYYY');
  }

  if (first.year() === second.year()) {
    if (first.month() === second.month()) {
      return first.format('MMM Do') + ' - ' + second.format('Do, YYYY');
    }
    return first.format('MMM Do') + ' - ' + second.format('MMM Do, YYYY');
  }
  return first.format('MMM Do, YYYY') + ' - ' + second.format('MMM Do, YYYY');
};

const getDateTime = (time?: string, format?: string): string =>
  time ? moment(time).format(format) : DEFAULT;

export const stringToUppercase = (str?: string): string | undefined =>
  str?.toUpperCase();

export const formatDateHyphDMY = (dateString: string): string =>
  getDateTime(dateString, 'DD-MMM-YYYY');

export const formatDateSpacedMD = (dateString: string): string =>
  getDateTime(dateString, 'MMM D');

export const formatUTCDateSpacedMD = (dateString: string): string =>
  moment.utc(dateString).format('MMM D');

export const formatDateSpacedMDY = (dateString: string): string =>
  getDateTime(dateString, 'MMM D YYYY');

export const formatTime = (dateString: string): string =>
  getDateTime(dateString, 'h:mm A');

export const formatUTCTime = (dateString: string): string =>
  moment.utc(dateString).format('h:mm A');

export const formatDateTime = (date: string): string =>
  getDateTime(date, 'M/D HH:mm');

export const formatDayOfWeek = (dateString: string): string =>
  getDateTime(dateString, 'ddd');

export const formatFullDateTime = (dateString: string): string =>
  getDateTime(dateString, 'MMM D YYYY h:mmA');

export const parseAndReturnDate = (dateString?: string, format?: string): Date | null => {
  if (dateString) {
    const momentDate = format ? moment(dateString, format) : moment(dateString);
    if (momentDate.isValid()) {
      return momentDate.toDate();
    }
  }
  return null;
};

export const parseAndFormatDateYMD = (
  date?: string,
  withTime?: boolean,
): string => {
  if (date) {
    const momentDate = moment(date);
    if (momentDate.isValid()) {
      return momentDate.format('YYYY-MM-DD' + (withTime ? 'THH:mm:ssZZ' : ''));
    }
  }
  return '';
};

export const isCritical = (date?: string): boolean => {
  if (date) {
    return moment(date).isBefore(
      moment()
        .utc()
        .add(5, 'minutes'),
    );
  }
  return false;
};

export const searchTermsOps = (searchTerms: string): string =>
  searchTerms.replace(/\sand\s/i, ' + ').replace(/\sor\s/i, ' | ');

export const departureAirportFull = (flight: Flight): string =>
  flight.departureAirportFull
    ? flight.departureAirportFull
    : `(${flight.departureAirportCode})`;

export const departureAirportCityState = (flight: Flight): string =>
  flight.departureAirportCityState
    ? flight.departureAirportCityState
    : `(${flight.departureAirportCode})`;

export const departureAirportCodeCityStateCountry = (flight: Flight): string =>
  flight.departureAirportCodeCityStateCountry
    ? flight.departureAirportCodeCityStateCountry
    : `(${flight.departureAirportCode})`;

export const arrivalAirportFull = (flight: Flight): string =>
  flight.arrivalAirportFull
    ? flight.arrivalAirportFull
    : `(${flight.arrivalAirportCode})`;

export const arrivalAirportCityState = (flight: Flight): string =>
  flight.arrivalAirportCityState
    ? flight.arrivalAirportCityState
    : `(${flight.arrivalAirportCode})`;

export const arrivalAirportCodeCityStateCountry = (flight: Flight): string =>
  flight.arrivalAirportCodeCityStateCountry
    ? flight.arrivalAirportCodeCityStateCountry
    : `(${flight.arrivalAirportCode})`;

export const airlineFull = (airline: Airline): string =>
  airline.full ? airline.full : `(${airline.code})`;

export const airlineName = (airline: Airline): string =>
  airline.name ? airline.name : `(${airline.code})`;

export const alertType = (alertType: string): string =>
  alertType
    .split('_')
    .map(word => word[0].toUpperCase() + word.substring(1).toLowerCase())
    .join(' ');

export const flightKey = (flightKey: FlightKey): string =>
  `${flightKey.airlineCode}-${flightKey.flightNumber} ${flightKey.departureAirportCode} to ${flightKey.arrivalAirportCode}`;

const formatValue = (value?: string): string =>
  (value || '').trim().toUpperCase();

export const flight = (airlineCode?: string, flightNumber?: string): string => {
  const code = formatValue(airlineCode);
  const number = formatValue(flightNumber);

  return [code, number].filter(Boolean).join('-');
};

export const flightPath = (
  departureAirportCode?: string,
  arrivalAirportCode?: string,
): string => {
  const departure = formatValue(departureAirportCode);
  const arrival = formatValue(arrivalAirportCode);

  return [departure, arrival].filter(Boolean).join(' to ');
};

export const flightStatusIndicatorStyle = (indicator: string): string => {
  const indicatorMap: Record<string, string> = {
    good: 'on-time',
    concerning: 'caution',
    bad: 'delayed',
  };

  return indicatorMap[indicator] || 'unknown';
};

export const connectionsStr = (connections?: number): string => {
  if (connections && connections > 0) {
    if (connections === 1) {
      return '(1 connection)';
    } else {
      return `(${connections} connections)`;
    }
  }
  return '';
};

export const connection = (
  connection: Connection | undefined,
  property: string,
  abbr = false,
): string => {
  if (!connection || !property) {
    return DEFAULT;
  }

  switch (property) {
    case 'in':
      return connection.in != null
        ? `${abbr ? 'CONN.' : 'CONNECTION'} in ${connection.in.toUpperCase()}`
        : '';

    case 'inFull':
      return connection.inFull ?? '';

    case 'connectionTime':
      return connection.connectionTime != null
        ? `${connection.connectionTime} min`
        : DEFAULT;

    case 'minConnectionTime':
      return connection.minConnectionTime != null
        ? `suggested minimum connection time is ${connection.minConnectionTime} minutes`
        : DEFAULT;

    case 'departureGate':
      return connection.departureGate ?? DEFAULT;

    case 'arrivalGate':
      return connection.arrivalGate ?? DEFAULT;

    default:
      return DEFAULT;
  }
};

export const stop = (stop: Stop | undefined, property: string): string => {
  if (!stop || !property) {
    return DEFAULT;
  }

  switch (property) {
    case 'in':
      return stop.in != null ? `STOP in ${stop.in.toUpperCase()}` : '';
    default:
      return DEFAULT;
  }
};

const getGateTimeLabel = (hop: Hop, depArr: DepArr): string => {
  if (
    (depArr === 'dep' && hop.actualGateDepartureDateTime) ||
    (depArr === 'arr' && hop.actualGateArrivalDateTime)
  ) {
    return 'ACTUAL GATE TIME';
  } else if (
    (depArr === 'dep' && hop.estimatedGateDepartureDateTime) ||
    (depArr === 'arr' && hop.estimatedGateArrivalDateTime)
  ) {
    return 'EST. GATE TIME';
  } else if (
    (depArr === 'dep' && hop.scheduledGateDepartureDateTime) ||
    (depArr === 'arr' && hop.scheduledGateArrivalDateTime)
  ) {
    return 'SCHED. GATE TIME';
  }
  return 'GATE TIME';
};

const getRunwayTimeLabel = (hop: Hop, depArr: DepArr): string => {
  if (
    (depArr === 'dep' && hop.actualRunwayDepartureDateTime) ||
    (depArr === 'arr' && hop.actualRunwayArrivalDateTime)
  ) {
    return 'ACTUAL RUNWAY TIME';
  } else if (
    (depArr === 'dep' && hop.estimatedRunwayDepartureDateTime) ||
    (depArr === 'arr' && hop.estimatedRunwayArrivalDateTime)
  ) {
    return 'EST. RUNWAY TIME';
  }
  return 'RUNWAY TIME';
};

export const flightTimeLabelStr = (
  hop: Hop,
  depArr: DepArr,
  property: string,
): string => {
  if (!hop || !property) {
    return 'PNR TIME';
  }

  switch (property) {
    case 'heading':
      return getGateTimeLabel(hop, depArr);
    case 'schGate':
      return 'SCHED. GATE TIME';
    case 'actEstGate':
      return getGateTimeLabel(hop, depArr);
    case 'actEstRunway':
      return getRunwayTimeLabel(hop, depArr);
    case 'pnr':
    default:
      return 'PNR TIME';
  }
};

export const flightTimeValue = (
  hop: Hop,
  depArr: DepArr,
  property: string,
): string => {
  if (!hop || !property) {
    return DEFAULT;
  }

  switch (property) {
    case 'heading':
      return depArr === 'dep'
        ? getDateTime(
            hop.actualGateDepartureDateTime ||
              hop.estimatedGateDepartureDateTime ||
              hop.scheduledGateDepartureDateTime ||
              hop.departureTime,
            'h:mm A',
          )
        : getDateTime(
            hop.actualGateArrivalDateTime ||
              hop.estimatedGateArrivalDateTime ||
              hop.scheduledGateArrivalDateTime ||
              hop.arrivalTime,
            'h:mm A',
          );

    case 'pnr':
      return depArr === 'dep'
        ? getDateTime(hop.departureTime, 'h:mm A')
        : getDateTime(hop.arrivalTime, 'h:mm A');

    case 'schGate':
      return depArr === 'dep'
        ? getDateTime(hop.scheduledGateDepartureDateTime, 'h:mm A')
        : getDateTime(hop.scheduledGateArrivalDateTime, 'h:mm A');

    case 'actEstGate':
      return depArr === 'dep'
        ? getDateTime(
            hop.actualGateDepartureDateTime ||
              hop.estimatedGateDepartureDateTime,
            'h:mm A',
          )
        : getDateTime(
            hop.actualGateArrivalDateTime || hop.estimatedGateArrivalDateTime,
            'h:mm A',
          );

    case 'actEstRunway':
      return depArr === 'dep'
        ? getDateTime(
            hop.actualRunwayDepartureDateTime ||
              hop.estimatedRunwayDepartureDateTime,
            'h:mm A',
          )
        : getDateTime(
            hop.actualRunwayArrivalDateTime ||
              hop.estimatedRunwayArrivalDateTime,
            'h:mm A',
          );

    default:
      return DEFAULT;
  }
};

export const issueFilter = (issue: Issue, property: string): string => {
  if (!issue || !property) {
    return DEFAULT;
  }

  switch (property) {
    case 'title':
    case 'type':
      return property === 'title'
        ? issue.type?.toUpperCase() ?? DEFAULT
        : issue.type ?? DEFAULT;

    case 'note':
      return issue.note ?? DEFAULT;

    case 'description':
      return issue.description ?? DEFAULT;

    case 'state':
      return issue.state ?? DEFAULT;

    case 'resolution':
      return issue.resolution?.prettyType ?? DEFAULT;

    case 'subResolution':
      return (
        ISSUE_ACTION_RES_ALL_OPTIONS[
          issue.resolution
            ?.subResolution as keyof typeof ISSUE_ACTION_RES_ALL_OPTIONS
        ] ?? DEFAULT
      );

    case 'extraMinutesAdded':
      return issue.resolution?.extraMinutesAdded?.toString() ?? DEFAULT;

    case 'extraMinutesAvoided':
      return issue.resolution?.extraMinutesAvoided?.toString() ?? DEFAULT;

    case 'travelerContacted':
      return issue.resolution?.travelerContacted?.toString() ?? DEFAULT;

    case 'itineraryUpdated':
      return issue.resolution?.pnrUpdated?.toString() ?? DEFAULT;

    case 'overnightAvoided':
      return issue.resolution?.overnightAvoided?.toString() ?? DEFAULT;

    case 'ratePaidInUsDollars':
      return issue.resolution?.ratePaidInUsDollars != null
        ? `${issue.resolution.ratePaidInUsDollars} USD`
        : DEFAULT;

    case 'flightsChanged':
      return issue.resolution?.flightsChanged?.toString() ?? DEFAULT;

    case 'hotelChanged':
      return issue.resolution?.hotelChanged?.toString() ?? DEFAULT;

    case 'newFlightsOnSameCarrier':
      return issue.resolution?.newFlightsOnSameCarrier?.toString() ?? DEFAULT;

    case 'groundTransportationChanged':
      return issue.resolution?.groundTransportationChanged?.toString() ?? DEFAULT;

    case 'optInTraveler':
      return issue.optInTraveler?.toString() ?? DEFAULT;

    case 'creationDate':
      return getDateTime(issue.creationDate, 'MMM D YYYY h:mm A');

    case 'resolveByTime':
      return getDateTime(issue.resolveByTime, 'MMM D YYYY h:mm A');

    default:
      return DEFAULT;
  }
};

export const timelineEventTypeFilter = (event: Event): string => {
  if (Object.hasOwn(event, 'comment')) {
    return 'Comment';
  }

  const eventTypeMap: Record<string, string> = {
    CREATED: 'Created',
    ASSIGNED: 'Updated',
    ASSISTED: 'Updated',
    UPDATED: 'Updated',
    REOPENED: 'Updated',
    MESSAGE_SENT: 'Message',
    RESOLVED: 'Resolution',
  };

  return (event.change && eventTypeMap[event.change]) || 'Unknown';
};

export const timelineEventDetailHeaderFilter = (
  event: Event,
  issue: Issue,
  username: string,
): string => {
  if (Object.hasOwn(event, 'comment')) {
    return `${username || 'An unknown someone'} left a comment.`;
  }

  if (event?.change != null) {
    const user = username || 'an unknown someone';

    switch (event.change) {
      case 'CREATED':
        return `The issue was created with type ${issue.type}.`;
      case 'ASSIGNED':
        return `The issue was assigned to ${user}.`;
      case 'ASSISTED':
        return `${user} assisted the issue.`;
      case 'UPDATED':
        return event.resolveByTime
          ? `The issue was deferred until ${getDateTime(
              event.resolveByTime, 'MMM D YYYY h:mm A',
            )} by ${user}.`
          : 'The issue was updated.';
      case 'MESSAGE_SENT':
        return `${user} sent a message${
          event.recipientAddress ? ` to ${event.recipientAddress}` : ''
        }.`;
      case 'REOPENED':
        return `The issue was re-opened by ${user}.`;
      case 'RESOLVED':
        return `The issue was resolved${
          event.prettyType ? ` as ${event.prettyType.toUpperCase()}` : ''
        } by ${user}.`;
      default:
        return 'Unknown';
    }
  }

  return 'Unknown';
};

export const timelineEventIssueFilter = (
  event: Event,
  property: string,
): string => {
  if (!event || !property) {
    return DEFAULT;
  }

  const propertyMap: Record<string, () => string> = {
    state: () => camelCaseItFilter(event.state),
    type: () => camelCaseItFilter(event.type),
    resolution: () => event.prettyType ?? DEFAULT,
    subResolution: () =>
      ISSUE_ACTION_RES_ALL_OPTIONS[
        event.subResolution as keyof typeof ISSUE_ACTION_RES_ALL_OPTIONS
      ] ?? DEFAULT,
    optInTraveler: () => camelCaseItFilter(event.optInTraveler?.toString()),
    resolveByTime: () => getDateTime(event.createTime, 'MMM D YYYY h:mm A'),
    extraMinutesAdded: () => event.extraMinutesAdded?.toString() ?? DEFAULT,
    extraMinutesAvoided: () => event.extraMinutesAvoided?.toString() ?? DEFAULT,
    travelerContacted: () => camelCaseItFilter(event.travelerContacted?.toString()),
    itineraryUpdated: () => camelCaseItFilter(event.pnrUpdated?.toString()),
    overnightAvoided: () => camelCaseItFilter(event.overnightAvoided?.toString()),
    ratePaidInUsDollars: () => `${event.ratePaidInUsDollars} USD`,
    flightsChanged: () => camelCaseItFilter(event.flightsChanged?.toString()),
    hotelChanged: () => camelCaseItFilter(event.hotelChanged?.toString()),
    newFlightsOnSameCarrier: () => camelCaseItFilter(event.newFlightsOnSameCarrier?.toString()),
    groundTransportationChanged: () => camelCaseItFilter(event.groundTransportationChanged?.toString()),
  };

  return propertyMap[property]?.() ?? DEFAULT;
};

export const camelCaseItFilter = (str?: string): string => {
  if (!str) {
    return DEFAULT
  }
  return str
    .replace(/(?:^\w|[A-Z]|\b\w|\s+)/g, (match, index) =>
      index === 0 ? match.toLowerCase() : match.toUpperCase(),
    )
    .replace(/\s+/g, '');
};

export const permissionNameFilter = (permissions: Permission[]): string[] => {
  return permissions
    .filter(perm => perm?.name)
    .map(perm => perm.name as string);
};

export const orgNodeNameFilter = (node: OrgNodeObj): string => {
  if (!node?.name) {
    return '';
  }

  const levelPrefix = node.level ? '/'.repeat(node.level) + ' ' : '';
  return `${levelPrefix}${node.name}`;
};

export const trustEndPointHTML = (source: string): { __html: string } => {
  return { __html: source };
};

export const prettyPrintArray = (array: any[]): string => {
  return Array.isArray(array) && array.length > 0 ? array.join(', ') : '';
};

export const waiverDatesListToPrettyDateFilter = (waiver: Waiver): string => {
  const { firstEffectiveDate, lastEffectiveDate, effectiveTravelDates } = waiver;

  const startDate = effectiveTravelDates?.[0] || firstEffectiveDate;
  const endDate = effectiveTravelDates?.[effectiveTravelDates.length - 1] || lastEffectiveDate;

  if (endDate) {
    return generateDateString(startDate || '', endDate || '');
  } else if (startDate) {
    return generateDateString(startDate);
  } else {
    return '';
  }
};

export const passengerDisplayName = (passenger: Passenger): string => {
  const names = [passenger.firstName, passenger.lastName].filter(Boolean);
  return names.length > 0 ? names.join(' ') : '(not provided)';
};

export const isUndefinedOrNullOrEmpty = (value: any) => {
  return (value === undefined || value === null || value === '');
}

export const validateText = (value: any, e?: React.ChangeEvent<HTMLInputElement>) => {
  if (value != '') {
    const minLength = e?.target.minLength ?? -1;
    const maxLength = e?.target.maxLength ?? -1;
    if ((minLength >= 0 && value.length < minLength) || (maxLength >= 0 && value.length > maxLength)) {
      return true;
    }
  }
  return false;
}

export const validateNumber = (value: string, e?: React.ChangeEvent<HTMLInputElement>) => {
  if (value != '') {
    if (isNaN(Number(value))) {
      return true;
    }
    const minLength = e?.target.minLength ?? -1;
    const maxLength = e?.target.maxLength ?? -1;
    if (
      (minLength >= 0 && value.length < minLength) || // Check if minLength is valid
      (maxLength >= 0 && value.length > maxLength)    // Check if maxLength is valid
    ) {
      return true;
    }
  }
  return false;
}

export const validateDate = (value: string) => {
  if (value && value != '') {
    if (moment(value).isValid()) {
      return false;
    } 
    return true;
  }
  return false;
}

export const validateEmail = (value: string) => {
  if (value != '') {
    if (!/^\S+@\S+\.\S+$/.test(value)) {
      return true;
    }
  }
  return false;
}

export const validateSelect = (value: any) => {
  const isObject = value instanceof Object && !Array.isArray(value);
  if (isObject || value == '') {
    return false;
  }
  return true;
}

export const validateField = (type: string, value: any, e?: React.ChangeEvent<HTMLInputElement>) => {
  switch (type) {
    case 'selectString':
    case 'text':
      return validateText(value, e);
    case 'number':
      return validateNumber(value, e);
    case 'date':
      return validateDate(value);
    case 'email':
      return validateEmail(value);
    case 'select':
      return validateSelect(value);
    default:
      return false;
  }
};